import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import URI from 'urijs';

import { useTheme } from '@reckon-web/theme';

import { useOpenJobDialog } from '../../hooks/use-open-job-dialog';
import { useOpenTaskDialog } from '../../hooks/use-open-task-dialog';
import { nameStatus } from '../../utils/name-status-utils';
import { sanitizer } from '../../utils/xss-utils';

import {
  useGetJobLinkText,
  useGetTaskLinkText,
  useGetUserLinkText,
} from './comment-text-renderer-hooks';

export const CommentTextRenderer: FC<{
  value: string;
  showEditedMarker?: boolean;
}> = ({ value, showEditedMarker }) => {
  // TODO?, Use what linkfiy used. Old lib in vuetify kinda sucked, but we had working code for it. Lib in linkify looks good
  // https://www.npmjs.com/package/linkify-it
  // https://github.com/tasti/react-linkify/blob/master/src/decorators/defaultMatchDecorator.js
  // https://github.com/tasti/react-linkify/blob/master/src/components/Linkify.jsx
  // The way they make actual react children is neat.
  // Probably better than this dangerously set inner html stuff
  // but... we don't want to parse the html we already have in the database. Nope. In theory it's simple HTML, but still nope.
  const [currentValue, setCurrentValue] = useState<string>('');
  const domNode = useRef<HTMLDivElement | null>(null);
  const getUserDetails = useGetUserLinkText();
  const getTaskDetails = useGetTaskLinkText();
  const getJobDetails = useGetJobLinkText();
  const theme = useTheme();
  const openJob = useOpenJobDialog();
  const openTask = useOpenTaskDialog();

  const processValue = useCallback(
    (value: string) => {
      const sanitized = sanitizer.process(value);

      const result = URI.withinString(sanitized, (url: string) => {
        // This URI library gets it wrong if we have a html tag after the hyperlink.
        // Like http://foo.com</div> or http://blah<br>
        // Normally, we'd just return `<a href="${url}" target="_blank" contenteditable="false">${ url }</a>`;
        // and move on. But, we cannot.
        // < is not valid in a uri, so, we'll split on < and only wrap the first bit of that array
        const splitOnLeftAngle = url.split('<');
        const firstElement = splitOnLeftAngle[0];
        // this library also is less good if there's an &nbsp; at the end of the url
        // which will happen if you put a space before hitting enter
        // which is normally ok, except if you do http://foo.com/, hit space, hit enter
        // then we end up with http://foo.com/&nbsp; in the plain text
        // & is ok for urls, so the library dutifully gives us the "wrong" url of http://foo.com/&nbsp
        // this is even worse if we've got some html tags too
        // as we end up trying to process  http://foo.com&nbsp;</div> as the url
        const firstElementSplitOnNbsp = firstElement.split('&nbsp');
        // wrap the actual url part in a link
        // target blank because we want them out in a new window.
        // contenteditable false because we want the whole url to go away if they hit delete/backspace
        // rel noopener noreferrer to stop a security exploit https://github.com/dbudwin/react-secure-link#the-exploit-
        let result = `<a href="${firstElementSplitOnNbsp[0]}" target="_blank" contenteditable="false" rel="noopener noreferrer">${firstElementSplitOnNbsp[0]}</a>`;
        if (firstElementSplitOnNbsp.length > 0) {
          // there was an nbsp in there.
          // Tack on everything joined up with nbsp so we get the space come out directly after the link
          result += '&nbsp' + firstElementSplitOnNbsp.slice(1).join('&nbsp');
        }
        if (splitOnLeftAngle.length > 1) {
          // there was a left angle bracket in there
          // Tack on everything joined up with an angle bracket
          result += '<' + splitOnLeftAngle.slice(1).join('<');
        }
        return result;
      });
      return (
        result +
        (showEditedMarker ? '<span class="edited-marker"> (edited)</span>' : '')
      );
    },
    [showEditedMarker]
  );

  const processMentions = useCallback(() => {
    if (!domNode.current) {
      return;
    }
    if (hasUnMounted.current) {
      return;
    }

    const spans = domNode.current.querySelectorAll('span[data-target-type]');
    for (const span of spans) {
      const spanAsSpan = span as HTMLSpanElement;
      const targetId = span.getAttribute('data-target-id');
      const targetType = span.getAttribute('data-target-type');
      if (targetId && targetType) {
        switch (targetType) {
          case 'user':
            // deliberately not awaiting this, we want to spin thru the spans as quick as possible
            getUserDetails(targetId).then(({ error, user }) => {
              if (error || !user || hasUnMounted.current) {
                return;
              }
              const name = `@${nameStatus(user)}`;
              spanAsSpan.innerText = name;
            });
            break;
          case 'task':
            getTaskDetails(targetId).then(({ error, job, debtor, task }) => {
              if (error || !job || !task || !debtor || hasUnMounted.current) {
                return;
              }
              const jobSuffix = job.WfDeletedOnUTC ? ' (deleted)' : '';
              const name = `@${task.name} (${job.name} (${debtor.name})${jobSuffix})`;
              spanAsSpan.innerText = name;
            });
            break;
          case 'job':
            getJobDetails(targetId).then(({ error, job, debtor }) => {
              if (error || !job || !debtor || hasUnMounted.current) {
                return;
              }
              const jobSuffix = job.WfDeletedOnUTC ? ' (deleted)' : '';
              const name = `@${job.name} (${debtor.name})${jobSuffix}`;
              spanAsSpan.innerText = name;
            });
            break;

          default:
            break;
        }
      }
    }
  }, [getJobDetails, getTaskDetails, getUserDetails]);

  const onCommentClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const targetAsElement = e.nativeEvent.target as HTMLElement;
    if (!targetAsElement) {
      return;
    }
    const targetId = targetAsElement.getAttribute('data-target-id');
    const targetType = targetAsElement.getAttribute('data-target-type');
    if (!targetId || !targetType) {
      return;
    }
    if (targetType === 'job') {
      openJob(targetId);
    } else if (targetType === 'task') {
      openTask(targetId);
    }
  };

  // when the incoming value changes, sanitize it and look for text links
  useEffect(() => {
    setCurrentValue(processValue(value));
  }, [processValue, value]);

  // then when the processed value changes, look for spans in the DOM
  // useLayoutEffect because...? Seems important to pause rendering while we identify spans, but maybe it is not
  useLayoutEffect(() => {
    processMentions();
  }, [processMentions, currentValue]);

  // maintain whether we are unmounted or not
  const hasUnMounted = useRef(false);
  useEffect(() => {
    // nothing to actually do here
    return () => {
      hasUnMounted.current = true;
    };
  }, []);
  return (
    <div
      ref={domNode}
      css={{
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        hyphens: 'auto',
        '& [data-target-id]': {
          // the injected spans have this attribute, and we're keeping it after xss runs
          // could start adding a class, but woried that class becomes an attack vector,
          // and also historic content do not have the classes (could add it on when we render, but no)
          color: theme.palette.text.link,
          '&:hover': {
            color: theme.palette.text.linkHover,
          },
        },
        '& [data-target-type="job"]': {
          cursor: 'pointer',
        },
        '& [data-target-type="task"]': {
          cursor: 'pointer',
        },
        '& .edited-marker': {
          color: theme.palette.text.dim,
          fontSize: '0.85em',
        },
        fontSize: theme.typography.fontSize.small,
      }}
      onClick={onCommentClick}
      dangerouslySetInnerHTML={{ __html: currentValue }}
    />
  );
};
